import { useState, useEffect, useCallback } from "react";
import { sanitizeTypstMarkup } from "@/lib/typstSanitizer";

// Declare global $typst from CDN
declare global {
  interface Window {
    $typst: any;
  }
}

const $typst = typeof window !== "undefined" ? window.$typst : null;

interface UseTypstCompilerReturn {
  isReady: boolean;
  error: string | null;
  compile: (typstContent: string) => Promise<Blob | null>;
  isCompiling: boolean;
  clearError: () => void;
}

/**
 * Convert a Blob to a base64 data URI
 * @param blob - The blob to convert
 * @returns Promise<string> - Base64 data URI (e.g., "data:image/png;base64,...")
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Detects and removes invalid '#' characters inside Typst code blocks { ... }.
 *
 * Typst Rules:
 * - Hashtags '#' are used to enter code mode from markup.
 * - Inside a code block { ... }, we are ALREADY in code mode.
 * - Therefore, using '#' inside { ... } is syntax error (unless in a string, comment, or content block).
 *
 * Example INVALID: { #let x = 1 }
 * Example VALID:   { let x = 1 }
 *
 * This is a client-side mirror of the server-side fixInvalidHashesInCode in validateTypst.ts
 */
function fixInvalidHashesInCode(content: string): {
  fixedContent: string;
  wasFixed: boolean;
  fixCount: number;
} {
  let fixedContentBuilder = "";
  let lastAppendIndex = 0;
  let fixCount = 0;

  // State Machine
  let inString = false;
  let inComment = false; // // comment
  let inBlockComment = false; // /* comment */
  let escapeNext = false;

  // Stack to track mode:
  // 'CODE' = inside { ... }
  // 'CONTENT' = inside [ ... ]
  // 'MATH' = inside $ ... $
  // Top-level is effectively 'CONTENT' (Markup)
  const modeStack: ("CODE" | "CONTENT" | "MATH")[] = ["CONTENT"];

  let i = 0;
  while (i < content.length) {
    const char = content[i];
    const nextChar = i + 1 < content.length ? content[i + 1] : "";

    // 1. Handle Escapes (skip next char)
    if (escapeNext) {
      escapeNext = false;
      i++;
      continue;
    }
    if (char === "\\") {
      escapeNext = true;
      i++;
      continue;
    }

    // 2. Handle Comments
    // Line comment //
    if (!inString && !inBlockComment && char === "/" && nextChar === "/") {
      inComment = true;
      i += 2; // Skip //
      continue;
    }
    // Block comment /*
    if (!inString && !inComment && char === "/" && nextChar === "*") {
      inBlockComment = true;
      i += 2; // Skip /*
      continue;
    }
    // End line comment
    if (inComment && char === "\n") {
      inComment = false;
      i++;
      continue;
    }
    // End block comment */
    if (inBlockComment && char === "*" && nextChar === "/") {
      inBlockComment = false;
      i += 2;
      continue;
    }

    // Skip parsing inside comments
    if (inComment || inBlockComment) {
      i++;
      continue;
    }

    // 3. Handle Strings
    if (char === '"') {
      inString = !inString;
      i++;
      continue;
    }

    // Skip parsing inside strings
    if (inString) {
      i++;
      continue;
    }

    // 4. Handle Structure (Braces, Brackets, Parentheses, Math)
    const currentMode = modeStack[modeStack.length - 1];

    if (char === "{" || char === "(") {
      // Enter CODE mode (both braces and parens are code contexts)
      if (currentMode !== "MATH") {
        modeStack.push("CODE");
      } else {
        modeStack.push("MATH"); // Keep math mode for matching stack
      }
    } else if (char === "}" || char === ")") {
      // Exit current mode
      if (modeStack.length > 1) {
        modeStack.pop();
      }
    } else if (char === "[") {
      // Enter CONTENT mode
      modeStack.push("CONTENT");
    } else if (char === "]") {
      // Exit CONTENT mode
      if (modeStack.length > 1) {
        modeStack.pop();
      }
    } else if (char === "$") {
      // Toggle MATH mode
      if (currentMode === "MATH") {
        // Exit Math
        if (modeStack.length > 1) {
          modeStack.pop();
        }
      } else {
        // Enter Math
        modeStack.push("MATH");
      }
    }

    // 5. Check for invalid '#'
    else if (char === "#") {
      if (currentMode === "CODE") {
        // FOUND INVALID HASH IN CODE BLOCK!
        // Auto-fix: Remove the '#'
        fixedContentBuilder += content.substring(lastAppendIndex, i);
        lastAppendIndex = i + 1; // Skip the hash
        fixCount++;
      }
    }

    i++;
  }

  // Append remainder
  fixedContentBuilder += content.substring(lastAppendIndex);

  return {
    fixedContent: fixedContentBuilder,
    wasFixed: fixCount > 0,
    fixCount,
  };
}

/**
 * Fetch a URL with retry logic and exponential backoff.
 * Used for fetching remote images that may fail due to network issues or cold starts.
 *
 * @param url - The URL to fetch
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param initialDelayMs - Initial delay in milliseconds before first retry (default: 500)
 * @returns Promise<Response> - The successful fetch response
 * @throws Error if all retries are exhausted
 */
async function fetchWithRetry(
  url: string,
  maxRetries: number = 3,
  initialDelayMs: number = 500
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[fetchWithRetry] Attempt ${attempt}/${maxRetries} for: ${url}`);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log(`[fetchWithRetry] ✅ Success on attempt ${attempt}`);
      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(`[fetchWithRetry] Attempt ${attempt} failed:`, lastError.message);

      if (attempt < maxRetries) {
        // Exponential backoff: 500ms, 1000ms, 2000ms, etc.
        const delayMs = initialDelayMs * Math.pow(2, attempt - 1);
        console.log(`[fetchWithRetry] Waiting ${delayMs}ms before retry...`);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }

  // All retries exhausted
  throw new Error(
    `Failed to fetch after ${maxRetries} attempts: ${lastError?.message || "Unknown error"}`
  );
}

/**
 * Preprocess Typst content to inline remote images as bytes literals.
 * This approach embeds image data directly in the Typst source code using
 * Typst's bytes() function and passes bytes directly to #image(), avoiding
 * the deprecated image.decode() function and the need for virtual file systems.
 *
 * @param content - The Typst content with #image("https://...") references
 * @returns Promise<string> - Processed content with inlined image data
 */
async function preprocessTypstContent(rawContent: string): Promise<string> {
  console.log("[preprocessTypstContent] Starting preprocessing...");

  // 1. Run deterministic sanitization (currency, brackets, illegal hashes, hallucinations)
  const sanitizeResult = sanitizeTypstMarkup(rawContent);
  let content = sanitizeResult.content;
  if (sanitizeResult.fixesApplied.length > 0) {
    console.log(
      `[preprocessTypstContent] Deterministic sanitizer applied: ${sanitizeResult.fixesApplied.join(", ")}`
    );
  }

  // AUTO-FIX: LLM hallucinations
  // The LLM sometimes generates invalid Typst syntax. Fix common patterns:

  // 1. table.of.contents() -> #outline() (Typst uses outline for TOC)
  if (content.includes("table.of.contents()")) {
    console.log(
      "[preprocessTypstContent] Found table.of.contents() hallucination, fixing to #outline()"
    );
    content = content.replace(/#?table\.of\.contents\(\)/g, "#outline()");
  }

  // 2. table.of(...) -> table(...) (table.of is not valid Typst)
  if (content.includes("table.of(")) {
    console.log("[preprocessTypstContent] Found table.of() hallucination, fixing to table()");
    content = content.replace(/table\.of\(/g, "table(");
  }

  // 3. line(indent: ...) -> remove the line entirely (indent is not a valid argument for line)
  // This hallucination appears as standalone lines like `#line(indent: auto)`
  if (content.includes("line(") && content.includes("indent:")) {
    console.log(
      "[preprocessTypstContent] Found line(indent:) hallucination, removing invalid lines"
    );
    content = content.replace(/#?line\(\s*indent:\s*[^)]+\)\s*\n?/g, "");
  }

  // 4. datetime(...) without # prefix -> add # prefix
  // The LLM sometimes puts datetime() inside text blocks without the # prefix
  // e.g., "*Date: datetime(year: 2026...)*" should have "#datetime(...)"
  // We need to be careful to only add # when it's missing (not already prefixed)
  if (content.includes("datetime(year:") && !content.includes("#datetime(year:")) {
    console.log("[preprocessTypstContent] Found datetime() without # prefix, adding # prefix");
    // Match datetime( that is NOT preceded by #
    content = content.replace(/(?<!#)datetime\(year:/g, "#datetime(year:");
  }

  // Find all #image("https://...") or #image("http://...") patterns
  const imageRegex = /#image\("(https?:\/\/[^"]+)"([^)]*)\)/g;
  const matches = [...content.matchAll(imageRegex)];

  if (matches.length === 0) {
    console.log("[preprocessTypstContent] No remote images found, skipping preprocessing");
    return content;
  }

  console.log(`[preprocessTypstContent] Found ${matches.length} remote image(s) to process`);

  let processedContent = content;
  const variableDefinitions: string[] = [];

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const imageUrl = match[1];
    const imageParams = match[2] || ""; // Capture parameters like ", width: 2in"

    try {
      console.log(
        `[preprocessTypstContent] Fetching image ${i + 1}/${matches.length}: ${imageUrl}`
      );

      // Fetch the image with retry logic and exponential backoff
      const response = await fetchWithRetry(imageUrl, 3, 500);

      const blob = await response.blob();
      console.log(
        `[preprocessTypstContent] Image ${i + 1} fetched: ${blob.size} bytes, type: ${blob.type}`
      );

      // Convert blob to Uint8Array
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Convert Uint8Array to hex bytes string for Typst
      const bytesString = Array.from(uint8Array)
        .map((b) => `0x${b.toString(16).padStart(2, "0")}`)
        .join(", ");

      // Create unique variable name
      const varName = `logo_data_${i}`;

      // Create Typst variable definition
      const varDefinition = `#let ${varName} = bytes((${bytesString}))`;
      variableDefinitions.push(varDefinition);

      // Replace ALL occurrences of #image("url", ...) with #image(logo_data_N, ...)
      // We use split key + join to replace globally
      const targetString = `#image("${imageUrl}"${imageParams})`;
      processedContent = processedContent
        .split(targetString)
        .join(`#image(${varName}${imageParams})`);

      console.log(
        `[preprocessTypstContent] ✅ Image ${i + 1} processed successfully as ${varName}`
      );
    } catch (error) {
      console.error(
        `[preprocessTypstContent] ❌ Failed to fetch/process image ${i + 1} after all retries:`,
        error
      );
      console.error(`[preprocessTypstContent] Image URL: ${imageUrl}`);

      // CRITICAL FIX: Remove the image call to prevent WASM "access denied" error
      // Replace the #image() call with a Typst comment explaining the failure
      const targetString = `#image("${imageUrl}"${imageParams})`;
      const fallbackText = `/* Image unavailable: ${imageUrl.split("?")[0]} */`;
      processedContent = processedContent.split(targetString).join(fallbackText);
      console.log(`[preprocessTypstContent] ⚠️ Replaced failed image with comment placeholder`);
    }
  }

  // Prepend all variable definitions to the content
  if (variableDefinitions.length > 0) {
    processedContent = variableDefinitions.join("\n") + "\n\n" + processedContent;
  }

  console.log("[preprocessTypstContent] ✅ Image preprocessing complete");
  console.log(`[preprocessTypstContent] Inlined ${variableDefinitions.length} image(s)`);
  return processedContent;
}

/**
 * Hook for compiling Typst markup to PDF using WASM in the browser.
 *
 * Features:
 * - Initializes the Typst WASM compiler once on mount
 * - Provides a compile function that takes Typst markup and returns a PDF Blob
 * - Tracks compiler readiness, compilation state, and errors
 * - Target compilation time: <100ms for typical documents
 *
 * @returns {UseTypstCompilerReturn} Compiler state and compile function
 *
 * @example
 * ```tsx
 * const { isReady, compile, isCompiling, error } = useTypstCompiler();
 *
 * const handleCompile = async () => {
 *   if (!isReady) return;
 *   const pdfBlob = await compile(typstMarkup);
 *   if (pdfBlob) {
 *     // Display PDF
 *   }
 * };
 * ```
 */
export function useTypstCompiler(): UseTypstCompilerReturn {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompiling, setIsCompiling] = useState(false);

  // Wait for the CDN script to load
  useEffect(() => {
    console.log("[useTypstCompiler] Initializing, checking for $typst...");

    const checkTypstReady = () => {
      if (window.$typst) {
        console.log("[useTypstCompiler] ✅ Typst compiler loaded from CDN successfully");
        console.log("[useTypstCompiler] $typst object:", window.$typst);
        setIsReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkTypstReady()) {
      return;
    }

    console.log("[useTypstCompiler] $typst not available yet, polling...");

    // Poll for $typst to be available
    let pollCount = 0;
    const interval = setInterval(() => {
      pollCount++;
      if (pollCount % 10 === 0) {
        console.log(
          `[useTypstCompiler] Still waiting for $typst... (${pollCount * 100}ms elapsed)`
        );
      }
      if (checkTypstReady()) {
        clearInterval(interval);
      }
    }, 100);

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.$typst) {
        console.error(
          "[useTypstCompiler] ❌ Typst compiler failed to load from CDN after 10 seconds"
        );
        console.error("[useTypstCompiler] Check if the CDN script is loaded in index.html");
        setError("Failed to load Typst compiler from CDN. Please refresh the page.");
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  /**
   * Compile Typst markup to PDF
   *
   * @param typstContent - The Typst markup string to compile
   * @returns Promise<Blob | null> - PDF Blob on success, null on failure
   */
  const compile = useCallback(
    async (typstContent: string): Promise<Blob | null> => {
      console.log("[useTypstCompiler] compile() called", {
        isReady,
        hasTypst: !!window.$typst,
        contentLength: typstContent?.length || 0,
      });

      if (!isReady || !window.$typst) {
        console.warn("[useTypstCompiler] ⚠️ Compiler not ready");
        return null;
      }

      if (!typstContent || typstContent.trim().length === 0) {
        console.warn("[useTypstCompiler] ⚠️ Empty Typst content provided");
        return null;
      }

      setIsCompiling(true);
      setError(null);

      try {
        const startTime = performance.now();

        console.log("[useTypstCompiler] Original content preview:", {
          contentPreview: typstContent.substring(0, 200),
          totalLength: typstContent.length,
        });

        // Preprocess content to inline remote images as bytes literals
        const processedContent = await preprocessTypstContent(typstContent);

        console.log("[useTypstCompiler] Calling $typst.pdf() with processed content:", {
          contentPreview: processedContent.substring(0, 500),
          totalLength: processedContent.length,
        });

        // Log the full processed content for debugging
        console.log("[useTypstCompiler] Full processed content:", processedContent);

        // Compile Typst markup to PDF
        const pdfData = await window.$typst.pdf({
          mainContent: processedContent,
        });

        const endTime = performance.now();
        const compilationTime = endTime - startTime;

        // Log compilation time for performance monitoring
        console.log(
          `[useTypstCompiler] ✅ Compilation completed in ${compilationTime.toFixed(2)}ms`
        );
        console.log(`[useTypstCompiler] PDF data size: ${pdfData?.length || 0} bytes`);

        // Convert Uint8Array to Blob
        const pdfBlob = new Blob([pdfData], { type: "application/pdf" });
        console.log(`[useTypstCompiler] Created PDF blob: ${pdfBlob.size} bytes`);

        setIsCompiling(false);
        return pdfBlob;
      } catch (err) {
        console.error("[useTypstCompiler] ❌ Compilation error:", err);

        // Try to parse SourceDiagnostic array from Typst compiler
        let errorMessage = "Failed to compile Typst document";
        if (Array.isArray(err)) {
          // Typst compiler returns an array of SourceDiagnostic objects
          const diagnostics = err.map((d: any) => d.message || String(d)).join("; ");
          errorMessage = `Typst compilation errors: ${diagnostics}`;
          console.error("[useTypstCompiler] Parsed diagnostics:", diagnostics);
        } else if (err instanceof Error) {
          errorMessage = err.message;
        } else if (typeof err === "string") {
          errorMessage = err;
        } else if (err && typeof err === "object") {
          // Try to extract any useful information
          const errObj = err as Record<string, unknown>;
          if ("message" in errObj) {
            errorMessage = String(errObj.message);
          } else {
            errorMessage = JSON.stringify(err).substring(0, 500);
          }
        }

        console.error("[useTypstCompiler] Error details:", {
          message: errorMessage,
          stack: err instanceof Error ? err.stack : undefined,
        });

        setError(errorMessage);
        setIsCompiling(false);
        return null;
      }
    },
    [isReady]
  );

  /**
   * Clear the current compiler error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isReady,
    error,
    compile,
    isCompiling,
    clearError,
  };
}
